import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { callAPI } from "../../helper/apiUtils";
import LanguageContext from "../../helper/LanguageContext";
import MaterialIcon from "../../components/MaterialIcon";
import { SET_OVERSIGHT_ROOM } from "../../store";
import { CompanyAdminEmbed } from "../Company/CompanyAdmin";
import BuildingMediaDocs from "./BuildingMediaDocs";
import "./OversightRoomDetail.css";

// Tab keys are the Admin Panel's room-details tab values; the host picks one by hash.
const TABS = [
  { key: "chats", label: (s) => s.chat || "Chat" },
  { key: "members", label: (s) => s.oversight_tab_members || "Mitglieder" },
  { key: "media", label: (s) => s.room_action_media || "Dateien" },
];

function roomName(room) {
  if (!room) return "";
  if (room.name || room.buildingName) return String(room.name || room.buildingName).trim();
  return [room.street, room.houseNo].filter(Boolean).join(" ");
}

/**
 * Company-admin oversight of one room (Räume > Oversight), laid out like a
 * regular room. Chat and members come from the Admin Panel's room details,
 * embedded without their own header and tab row; files use the room's own
 * Dateien view (all members' files, read-only).
 */
export default function OversightRoomDetail({ room }) {
  const { strings, isGerm } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const [tab, setTab] = useState("chats");
  const [name, setName] = useState(room.name || "");

  // The list hands over the name; the room record fills it in otherwise.
  useEffect(() => {
    let alive = true;
    setName(room.name || "");
    if (room.name) return undefined;
    callAPI(`/building/one/${room.id}`, {}, "GET")
      .then(({ data }) => {
        if (alive) setName(roomName((data && data.data) || data));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [room.id, room.name]);

  const close = () => dispatch({ type: SET_OVERSIGHT_ROOM, payload: null });

  return (
    <div className={`room-workspace oversight-room${room.isInternal ? " oversight-room--internal" : ""}`}>
      <div className="room-workspace-header">
        <div className="room-header-title-row room-header-title-row--workspace">
          <div className="room-header-title-copy">
            <h3>{name || " "}</h3>
            <p className="oversight-room__subtitle">
              <span className="material-symbols-outlined oversight-room__eye" aria-hidden="true">visibility</span>
              {strings.rooms_oversight || "Supervision"}
              {room.companyName ? <span className="oversight-room__company"> · {room.companyName}</span> : null}
            </p>
          </div>
          <div className="room-header-title-actions">
            <button
              type="button"
              className="oversight-room__close"
              onClick={close}
              aria-label={strings.close || "Schließen"}
              title={strings.close || "Schließen"}
            >
              <MaterialIcon icon="close" size={22} color="currentColor" weight={300} />
            </button>
          </div>
        </div>
      </div>
      <div className="oversight-room__card">
        <div className="oversight-room__tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`kj-tab${tab === t.key ? " is-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label(strings)}
            </button>
          ))}
        </div>
        {tab === "media" ? (
          <div className="oversight-room__body oversight-room__body--files">
            <BuildingMediaDocs
              key={room.id}
              project_id={room.id}
              hideBack
              hideAutoSync
              viewByOwner
              isInternal={room.isInternal}
            />
          </div>
        ) : (
          <div className="oversight-room__body">
            <CompanyAdminEmbed
              key={`${room.companyId}-${room.id}`}
              companyId={room.companyId}
              route={`/admin/rooms/${room.id}#${tab}`}
              bare
              flush
              roomShell
              className="is-settings"
            />
          </div>
        )}
      </div>
    </div>
  );
}
